import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, Check, Crown, Sparkles, Lock, Tag, X as XIcon } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { ClayButton } from "../components/ClayButton";
import { PLANS, createCheckout, validateCoupon } from "../lib/billing";
import { useEntitlement } from "../contexts/EntitlementContext";
import { useAuth } from "../contexts/AuthContext";

const FEATURES = [
  "Rondas ilimitadas en Primaria",
  "Acceso completo a Secundaria (Álgebra y más)",
  "Todas las medallas y rachas",
  "Nuevos temas conforme se agreguen",
  "Sin anuncios, siempre",
];

export default function Upgrade() {
  const navigate = useNavigate();
  const location = useLocation();
  const { entitlement } = useEntitlement();
  const { status } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState("yearly");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Coupon state
  const [couponOpen, setCouponOpen] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [applied, setApplied] = useState(null); // { code, discount, final_amount, percent_off, ... }
  const [couponError, setCouponError] = useState("");
  const [validating, setValidating] = useState(false);

  const reason = location.state?.reason;

  const revalidate = async (code, plan) => {
    try {
      const data = await validateCoupon(code, plan);
      if (data.valid) {
        setApplied({ ...data });
        setCouponError("");
      } else {
        setApplied(null);
        setCouponError("Cupón no válido para este plan");
      }
    } catch {
      setApplied(null);
    }
  };

  const handleApplyCoupon = async () => {
    setCouponError("");
    const code = couponInput.trim();
    if (!code) return;
    setValidating(true);
    try {
      const data = await validateCoupon(code, selectedPlan);
      if (!data.valid) {
        setApplied(null);
        let msg = "Este cupón ya no está disponible.";
        if (data.reason === "not_found") {
          msg = "No encontramos ese cupón. Revisa que esté bien escrito.";
        } else if (data.reason === "plan_not_eligible") {
          msg = "Este cupón solo aplica al plan Mensual. Cambia de plan para usarlo.";
        }
        setCouponError(msg);
      } else {
        setApplied(data);
        setCouponInput(data.code);
      }
    } catch {
      setCouponError("No pudimos verificar el cupón. Intenta de nuevo.");
    } finally {
      setValidating(false);
    }
  };

  // Auto-remove the coupon if the user switches to an ineligible plan
  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    if (applied && planId === "yearly") {
      setApplied(null);
      setCouponError("");
      setCouponInput("");
      setCouponOpen(false);
    } else if (applied) {
      revalidate(applied.code, planId);
    }
  };

  const handleRemoveCoupon = () => {
    setApplied(null);
    setCouponInput("");
    setCouponError("");
  };

  const handleSubscribe = async () => {
    if (status !== "cloud") {
      navigate("/", { state: { redirect: "/upgrade" } });
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { url } = await createCheckout(selectedPlan, applied?.code || null);
      window.location.href = url;
    } catch (e) {
      setError(e?.response?.data?.detail || "No pudimos iniciar el pago. Intenta de nuevo.");
      setLoading(false);
    }
  };

  const selectedPrice = PLANS.find((p) => p.id === selectedPlan)?.price || 0;
  const finalPrice = applied ? applied.final_amount : selectedPrice;

  return (
    <PageShell>
      <div className="mt-4">
        <ClayButton variant="ghost" onClick={() => navigate("/dashboard")} data-testid="upgrade-back-btn">
          <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          Volver
        </ClayButton>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-3xl mx-auto mt-4 sm:mt-6"
      >
        {/* Hero */}
        <div className="text-center mb-6 sm:mb-8">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", bounce: 0.5 }}
            className="inline-flex w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-gradient-to-br from-[#FFD700] to-[#FF9500] items-center justify-center mb-4 shadow-lg"
          >
            <Crown className="w-12 h-12 sm:w-14 sm:h-14 text-white" strokeWidth={2.5} />
          </motion.div>
          <h1
            className="font-heading text-3xl sm:text-5xl font-bold text-[#1C1C1E] mb-2"
            data-testid="upgrade-title"
          >
            Hazte Premium
          </h1>
          <p className="text-base sm:text-lg text-[#5C5C60] max-w-xl mx-auto">
            Practica sin límites y desbloquea todos los temas de Secundaria.
          </p>
        </div>

        {/* Reason banner */}
        {reason === "daily_limit" && (
          <div
            className="bg-[#FFF6CC] border-2 border-[#FFD700] rounded-2xl px-4 py-3 mb-6 flex items-start gap-3"
            data-testid="upgrade-reason-daily"
          >
            <Sparkles className="w-5 h-5 text-[#7A5C00] shrink-0 mt-0.5" strokeWidth={3} />
            <p className="text-sm font-bold text-[#7A5C00]">
              Ya completaste tu ronda gratis de hoy. ¡Hazte Premium para seguir practicando!
            </p>
          </div>
        )}
        {reason === "secundaria_locked" && (
          <div
            className="bg-[#E9E3FF] border-2 border-[#7B61FF] rounded-2xl px-4 py-3 mb-6 flex items-start gap-3"
            data-testid="upgrade-reason-secundaria"
          >
            <Lock className="w-5 h-5 text-[#3D2EA0] shrink-0 mt-0.5" strokeWidth={3} />
            <p className="text-sm font-bold text-[#3D2EA0]">
              Secundaria es contenido Premium. Suscríbete para acceder a Álgebra y los próximos temas.
            </p>
          </div>
        )}

        {/* Trial info if active */}
        {entitlement?.trial_active && (
          <div
            className="bg-[#E8F9EE] border-2 border-[#34C759] rounded-2xl px-4 py-3 mb-6 text-center"
            data-testid="upgrade-trial-info"
          >
            <p className="text-sm font-bold text-[#1A6B2F]">
              🎁 Te quedan <strong>{entitlement.trial_days_left} día{entitlement.trial_days_left === 1 ? "" : "s"}</strong> de prueba gratis.
              ¡Sigue practicando!
            </p>
          </div>
        )}

        {/* Plans */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6" data-testid="upgrade-plans">
          {PLANS.map((plan) => {
            const isSelected = selectedPlan === plan.id;
            return (
              <button
                key={plan.id}
                onClick={() => handleSelectPlan(plan.id)}
                className={`relative text-left rounded-3xl p-5 sm:p-6 border-4 transition-all ${
                  isSelected
                    ? "bg-gradient-to-br from-[#FFF6CC] to-white border-[#FFD700] shadow-[0_15px_30px_-15px_rgba(255,165,0,0.4)]"
                    : "bg-white border-gray-100 hover:border-gray-200"
                }`}
                data-testid={`upgrade-plan-${plan.id}`}
              >
                {plan.badge && (
                  <span className="absolute -top-3 left-5 bg-[#FF9500] text-white text-xs font-bold uppercase tracking-wider rounded-full px-3 py-1 shadow">
                    {plan.badge}
                  </span>
                )}
                <div className="flex items-center justify-between mb-2">
                  <span className="font-heading text-2xl font-bold text-[#1C1C1E]">
                    {plan.label}
                  </span>
                  <span
                    className={`w-6 h-6 rounded-full border-4 ${
                      isSelected ? "bg-[#FFD700] border-[#FFD700]" : "border-gray-300"
                    } flex items-center justify-center`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={4} />}
                  </span>
                </div>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-heading text-4xl font-bold text-[#1C1C1E]">
                    ${plan.price}
                  </span>
                  <span className="text-sm font-bold text-[#5C5C60]">{plan.currency}</span>
                  <span className="text-sm text-[#5C5C60]">
                    /{plan.id === "monthly" ? "mes" : "año"}
                  </span>
                </div>
                {plan.id === "yearly" && (
                  <p className="text-xs font-bold text-[#1A6B2F] mb-2">
                    ≈ ${plan.perMonth} MXN/mes
                  </p>
                )}
                <p className="text-sm text-[#5C5C60]">{plan.description}</p>
              </button>
            );
          })}
        </div>

        {/* Features */}
        <div className="bg-white rounded-3xl border-4 border-white shadow-md p-5 sm:p-6 mb-6">
          <p className="font-heading text-xl font-bold text-[#1C1C1E] mb-3">
            Lo que incluye:
          </p>
          <ul className="space-y-2.5">
            {FEATURES.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-[#E8F9EE] flex items-center justify-center shrink-0 mt-0.5">
                  <Check className="w-4 h-4 text-[#34C759]" strokeWidth={4} />
                </span>
                <span className="text-base text-[#1C1C1E]">{f}</span>
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <div
            className="bg-[#FFEAEA] border-2 border-[#FF6B6B] rounded-2xl px-4 py-3 text-sm font-bold text-[#803535] mb-4"
            data-testid="upgrade-error"
          >
            {error}
          </div>
        )}

        {/* Coupon block — only available for monthly plan */}
        {selectedPlan === "monthly" && (
        <div className="mb-4" data-testid="upgrade-coupon-block">
          {!couponOpen && !applied && (
            <button
              onClick={() => setCouponOpen(true)}
              className="inline-flex items-center gap-2 text-sm font-bold text-[#FF9500] hover:text-[#B05A00] transition-colors"
              data-testid="upgrade-coupon-toggle"
            >
              <Tag className="w-4 h-4" strokeWidth={3} />
              ¿Tienes un cupón?
            </button>
          )}

          <AnimatePresence>
            {couponOpen && !applied && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-white border-2 border-[#FFEEDC] rounded-2xl p-4 mt-2">
                  <label className="block text-xs font-bold uppercase tracking-wider text-[#5C5C60] mb-2">
                    Código de cupón
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder="EJ. MATE50"
                      maxLength={40}
                      className="flex-1 bg-[#FFF9E6] border-2 border-[#F2ECE4] rounded-xl px-3 py-2 font-bold uppercase tracking-wider focus:border-[#FF9500] focus:outline-none transition-colors"
                      data-testid="upgrade-coupon-input"
                      onKeyDown={(e) => e.key === "Enter" && handleApplyCoupon()}
                    />
                    <button
                      onClick={handleApplyCoupon}
                      disabled={validating || !couponInput.trim()}
                      className="bg-[#FF9500] hover:bg-[#B05A00] text-white font-bold rounded-xl px-4 py-2 transition-colors disabled:opacity-50"
                      data-testid="upgrade-coupon-apply"
                    >
                      {validating ? "..." : "Aplicar"}
                    </button>
                  </div>
                  {couponError && (
                    <p className="mt-2 text-xs font-bold text-[#803535]" data-testid="upgrade-coupon-error">
                      {couponError}
                    </p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {applied && (
            <motion.div
              initial={{ opacity: 0, y: -5 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-[#E8F9EE] border-2 border-[#34C759] rounded-2xl p-4 mt-2 flex items-center gap-3"
              data-testid="upgrade-coupon-applied"
            >
              <div className="w-10 h-10 rounded-xl bg-[#34C759] flex items-center justify-center shrink-0">
                <Tag className="w-5 h-5 text-white" strokeWidth={3} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading text-base font-bold text-[#1A6B2F]">
                  Cupón {applied.code} aplicado
                </p>
                <p className="text-sm text-[#1A6B2F]">
                  {applied.percent_off
                    ? `${applied.percent_off}% de descuento`
                    : `−$${applied.discount} MXN`}
                  {" · "}Ahorras ${applied.discount} MXN
                </p>
              </div>
              <button
                onClick={handleRemoveCoupon}
                className="w-8 h-8 rounded-full bg-white border-2 border-[#34C759] flex items-center justify-center hover:bg-[#FFEAEA] transition-colors"
                data-testid="upgrade-coupon-remove"
                aria-label="Quitar cupón"
              >
                <XIcon className="w-4 h-4 text-[#1A6B2F]" strokeWidth={3} />
              </button>
            </motion.div>
          )}
        </div>
        )}

        <ClayButton
          onClick={handleSubscribe}
          disabled={loading}
          size="lg"
          className="w-full"
          data-testid="upgrade-subscribe-btn"
        >
          <Crown className="w-5 h-5" strokeWidth={3} />
          {loading
            ? "Redirigiendo..."
            : status !== "cloud"
            ? "Crear cuenta para suscribirme"
            : applied
            ? `Suscribirme · $${finalPrice} MXN`
            : `Suscribirme · $${selectedPrice} MXN`}
        </ClayButton>

        <p className="text-xs text-center text-[#5C5C60] mt-4">
          Pago seguro vía Stripe. Pago único — no se renueva automáticamente.
          <br />
          Al suscribirte aceptas los{" "}
          <button
            onClick={() => navigate("/terminos")}
            className="underline font-bold hover:text-[#FF9500]"
            data-testid="upgrade-terms-link"
          >
            Términos y Política de Reembolso
          </button>
          .
        </p>
      </motion.div>
    </PageShell>
  );
}
